import { z } from 'zod'
import {
  Carrier,
  CarrierApi,
  Configuration,
} from '@sennder/carrier-profile-public-axios-client'

import {
  CARRIER_PROFILE_PUBLIC_SERVICE_AUDIENCE,
  CARRIER_PROFILE_PUBLIC_SERVICE_URL,
} from '@/common/config'
import { getAuth0Header } from '@/services/tokenManager'
import { logger } from './logger/loggers'

class CarrierProfilePublicService {
  private async getApiConfig(): Promise<Configuration> {
    const accessToken = await this.getCPPSToken()
    const options = {
      headers: {
        Authorization: accessToken,
      },
    }
    return new Configuration({
      basePath: CARRIER_PROFILE_PUBLIC_SERVICE_URL,
      baseOptions: options,
    })
  }

  async carrierApiInstance(): Promise<CarrierApi> {
    const apiConfig = await this.getApiConfig()
    return new CarrierApi(apiConfig)
  }

  async getCPPSToken(): Promise<string> {
    const authTokenHeader = await getAuth0Header({
      audience: CARRIER_PROFILE_PUBLIC_SERVICE_AUDIENCE,
    })
    if (!authTokenHeader) {
      throw new Error('[CPPS]: Cant build a request. Token is empty')
    }

    return authTokenHeader
  }

  async getCarrier(carrierId: string): Promise<Carrier> {
    try {
      const api = await this.carrierApiInstance()
      const { data } = await api.readCarrierV1CarriersCarrierIdGet(carrierId)
      saveCarrierToLocalStorage(carrierId, data)
      return data
    } catch (error: any) {
      const errorMessage = `Error getting carrier data from CPPS ${
        error.response?.data || error.message
      }`

      const cachedCarrier = getCarrierFromLocalStorage(carrierId)
      if (cachedCarrier) {
        logger.error(errorMessage, { error })
        logger.info(
          `[CPPS]: returning cached carrier data for carrier ${carrierId}`,
          {}
        )
        return cachedCarrier
      }

      throw new Error(errorMessage)
    }
  }

  async exchangeMothershipCarrierIdForNewUUID(
    mothershipCarrierId: number
  ): Promise<string> {
    try {
      const api = await this.carrierApiInstance()
      const response =
        await api.mothershipIdToCpsIdV1CarriersIdFromMothershipMothershipIdGet(
          mothershipCarrierId
        )
      const carrierId = response.data.id
      saveCarrierIdToLocalStorage(mothershipCarrierId, carrierId)
      return carrierId
    } catch (error: any) {
      const errorMessage = `Error exchanging carrier mothership id for uuid CPPS ${JSON.stringify(
        error.response?.data ?? error.message
      )}`

      const cachedCarrierId = getCarrierIdFromLocalStorage(mothershipCarrierId)
      if (cachedCarrierId) {
        logger.error(errorMessage, { error })
        logger.info(
          `[CPPS]: returning cached carrier id data for mothership carrier id: ${mothershipCarrierId}`,
          {}
        )
        return cachedCarrierId
      }

      throw new Error(errorMessage)
    }
  }
}

const getCarrierStorageKey = (carrierId: string) => `cpps_carrier_${carrierId}`

const getCarrierIdStorageKey = (mothershipCarrierId: number) =>
  `cpps_carrier_id_${mothershipCarrierId}`

// This is a loose schema of Carrier object, just to validate the overall shape of the object
const CarrierSchema = z.object({
  id: z.string(),
  type: z.string(),
  company_name: z.string(),
  cooperation_type: z.array(z.string()).nullable().optional(),
  website: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  certified_email: z.string().nullable().optional(),
  traffic_office_email: z.string().nullable().optional(),
  address: z.record(z.any()).nullable().optional(),
  legal_representative: z.string().nullable().optional(),
  emergency_phone: z.string().nullable().optional(),
  carrier_manager: z.string().nullable().optional(),
  salesforce_id: z.string().nullable().optional(),
  mothership_id: z.number().nullable().optional(),
  is_international: z.boolean().nullable().optional(),
  primary_carrier_id: z.string().nullable().optional(),
  secondary_carrier_id: z.string().nullable().optional(),
  is_denylisted: z.boolean().nullable().optional(),
  vetting_status: z.string().nullable().optional(),
  vat: z.record(z.any()),
  billing: z.record(z.any()),
  fleet_overview: z.record(z.any()),
  gps_integration: z.record(z.any()),
  certifications: z.record(z.any()).nullable().optional(),
  safety_procedure: z.record(z.any()).nullable().optional(),
  reason_for_denylisting_masked: z.number().nullable().optional(),
})

function getCarrierIdFromLocalStorage(mothershipCarrierId: number) {
  try {
    const carrierId = localStorage.getItem(
      getCarrierIdStorageKey(mothershipCarrierId)
    )
    if (!carrierId) {
      return
    }
    return carrierId
  } catch (error: any) {
    logger.error('Error getting carrier id from local storage', {
      error,
    })
  }
}

function saveCarrierIdToLocalStorage(
  mothershipCarrierId: number,
  carrierId: string
) {
  try {
    localStorage.setItem(getCarrierIdStorageKey(mothershipCarrierId), carrierId)
  } catch (error: any) {
    logger.error('Error saving carrier id to local storage', {
      error,
    })
  }
}

function getCarrierFromLocalStorage(carrierId: string) {
  try {
    const carrier = localStorage.getItem(getCarrierStorageKey(carrierId))
    if (!carrier) {
      return
    }
    const { data, error } = CarrierSchema.safeParse(JSON.parse(carrier))
    if (error) {
      logger.error('Error parsing carrier from local storage', {
        error: error.issues,
      })
    }
    return data as Carrier | undefined
  } catch (error: any) {
    logger.error('Error getting carrier from local storage', {
      error,
    })
  }
}

function saveCarrierToLocalStorage(carrierId: string, carrier: Carrier) {
  try {
    localStorage.setItem(
      getCarrierStorageKey(carrierId),
      JSON.stringify(carrier)
    )
  } catch (error: any) {
    logger.error('Error saving carrier to local storage', {
      error,
    })
  }
}

export const carrierProfilePublicService = new CarrierProfilePublicService()
